import React, { useState, useEffect } from 'react';
import {
  Form,
  AutoComplete,
  FormInstance,
  ColProps,
  AutoCompleteProps,
} from 'antd';
import { isEmpty, debounce } from 'lodash-es';
import usePlacesAutocomplete, {
  getLatLng,
  getGeocode,
} from 'use-places-autocomplete';
import {
  splitAddressFromGoogleMap,
  splitCompanyAddressFromGoogleMap,
} from 'utils/dataUtils';
import { PLACE_DEFAULT_OPTIONS } from 'configs/localData/constant';
import { useTranslation } from 'react-i18next';
import { NamePath } from 'antd/lib/form/interface';
import { Rule } from 'antd/lib/form';
import { Geolocation } from 'types/centres';
import FormGoogleAutoCompleteStyles from './styles';

export interface FormGoogleAutocompleteProps extends AutoCompleteProps {
  header?: string;
  required?: boolean;
  messageRequire?: string;
  placeholder?: string;
  wrapperCol?: ColProps;
  form?: FormInstance;
  record?: {
    geolocation?: Geolocation;
  };
  source?: NamePath;
  rules?: Rule[];
  onSelectAddress?: (value?: object) => void;
  isEditCompanyAddress?: boolean;
}

const FormGoogleAutocomplete = ({
  header,
  required,
  messageRequire,
  placeholder,
  wrapperCol,
  defaultValue,
  form,
  record,
  source,
  rules,
  onSelectAddress,
  disabled,
  isEditCompanyAddress,
}: FormGoogleAutocompleteProps) => {
  const { t } = useTranslation();
  const [coordinates, setCoordinates] = useState({});
  useEffect(() => {
    if (!isEmpty(record) && isEmpty(coordinates)) {
      setCoordinates({
        lat: record?.geolocation?.latitude,
        lng: record?.geolocation?.longitude,
      });
      form.setFieldsValue({
        coordinates: {
          latitude: record?.geolocation?.latitude,
          longitude: record?.geolocation?.longitude,
        },
      });
    }
  }, [record, coordinates, form]);

  const {
    suggestions: { status, data },
    setValue,
  } = usePlacesAutocomplete({
    requestOptions: PLACE_DEFAULT_OPTIONS,
  });

  const handleSearch = (e) => {
    setValue(e);
  };

  const handleSearchDebounce = debounce(handleSearch, 600);
  const handleSelect = async (e) => {
    try {
      const results = await getGeocode({ address: e });
      const coordinates = await getLatLng(results[0]);

      setCoordinates(coordinates);

      form.setFieldsValue({
        geolocation: {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
      });

      onSelectAddress({
        coordinates,
        address: results?.[0]?.formatted_address || e,
        addressObj: isEditCompanyAddress
          ? splitCompanyAddressFromGoogleMap(results[0].address_components)
          : splitAddressFromGoogleMap(results[0].address_components),
      });
    } catch (error) {
      throw Error(error);
    }
  };

  const renderSuggestions = data.reduce((suggestionList, suggestion) => {
    const isFullAddress = suggestion.description.split(',').length >= 3;

    if (isFullAddress) {
      const newSuggestion = { value: suggestion.description };
      suggestionList.push(newSuggestion);
    }

    return suggestionList;
  }, []);

  return (
    <FormGoogleAutoCompleteStyles>
      <Form.Item
        wrapperCol={wrapperCol}
        label={t(header)}
        name={source}
        initialValue={defaultValue}
        rules={[
          {
            required,
            message: t(messageRequire),
          },
          ...rules,
        ]}
      >
        <AutoComplete
          options={status === 'OK' ? renderSuggestions : []}
          onSearch={handleSearchDebounce}
          onSelect={handleSelect}
          placeholder={t(placeholder)}
          disabled={disabled}
        />
      </Form.Item>
    </FormGoogleAutoCompleteStyles>
  );
};

FormGoogleAutocomplete.defaultProps = {
  required: true,
  messageRequire: 'input.address.validateMsg.required',
  wrapperCol: { span: 24 },
  rules: [],
  onSelectAddress: () => null,
};

export default FormGoogleAutocomplete;
